import React, { useMemo } from "react";
import ReactSelect from "react-select";

/**
 * Выпадающий список — Soft Slate (AUFB-67).
 *
 * Заменяет нативный <select>: его раскрывающийся список рисует
 * операционная система, поэтому в Windows он оставался белой панелью с
 * синим выделением даже в тёмной теме. Здесь список — обычный DOM, и он
 * подчиняется токенам.
 *
 * Цвета берутся из CSS-переменных, а не из хардкода: react-select не
 * умеет читать var() в своей теме, но styles-объект возвращает обычные
 * CSS-значения, и var() в них работает.
 *
 * Совместимость с нативным select: value/onChange принимают и отдают
 * простое значение (строку или число), а не объект — чтобы вызывающий
 * код не пришлось переписывать целиком.
 */

/** Общие стили. Вынесены наружу, чтобы не пересоздавать на каждый рендер. */
const styles = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    background: "var(--c-input)",
    borderColor: state.isFocused ? "var(--c-accent)" : "var(--c-border)",
    borderRadius: "var(--c-radius-pill)",
    boxShadow: state.isFocused
      ? "0 0 0 3px color-mix(in srgb, var(--c-accent) 25%, transparent)"
      : "none",
    cursor: "pointer",
    transition: "border-color 140ms ease, box-shadow 140ms ease",
    ":hover": { borderColor: state.isFocused ? "var(--c-accent)" : "var(--c-text-muted)" },
  }),
  valueContainer: (base) => ({ ...base, padding: "2px 6px 2px 14px" }),
  singleValue: (base) => ({ ...base, color: "var(--c-text)", fontSize: 14 }),
  placeholder: (base) => ({ ...base, color: "var(--c-text-muted)", fontSize: 14 }),
  input: (base) => ({ ...base, color: "var(--c-text)", fontSize: 14, margin: 0 }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: "var(--c-text-muted)",
    padding: "0 12px 0 4px",
    transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : null,
    transition: "transform 160ms ease",
    ":hover": { color: "var(--c-text-sub)" },
  }),
  clearIndicator: (base) => ({
    ...base,
    color: "var(--c-text-muted)",
    padding: 4,
    ":hover": { color: "var(--c-danger)" },
  }),
  menu: (base) => ({
    ...base,
    background: "var(--c-card)",
    border: "1px solid var(--c-border)",
    borderRadius: "var(--c-radius-card)",
    boxShadow: "var(--c-shadow-md)",
    overflow: "hidden",
    zIndex: 60,
  }),
  menuList: (base) => ({ ...base, padding: 6, maxHeight: 300 }),
  option: (base, state) => ({
    ...base,
    borderRadius: "var(--c-radius-item)",
    padding: "9px 12px",
    fontSize: 14,
    cursor: "pointer",
    // Выбранный — жёлтая плашка, как активный пункт меню.
    background: state.isSelected
      ? "var(--c-accent)"
      : state.isFocused
      ? "var(--c-card-hover)"
      : "transparent",
    color: state.isSelected ? "var(--c-accent-on)" : "var(--c-text)",
    fontWeight: state.isSelected ? 700 : 400,
    ":active": { background: state.isSelected ? "var(--c-accent)" : "var(--c-card-hover)" },
  }),
  noOptionsMessage: (base) => ({ ...base, color: "var(--c-text-muted)", fontSize: 14 }),
  multiValue: (base) => ({
    ...base,
    background: "var(--c-card-hover)",
    borderRadius: "var(--c-radius-pill)",
    padding: "1px 2px 1px 6px",
  }),
  multiValueLabel: (base) => ({ ...base, color: "var(--c-text)", fontSize: 13 }),
  multiValueRemove: (base) => ({
    ...base,
    borderRadius: "var(--c-radius-pill)",
    color: "var(--c-text-muted)",
    ":hover": { background: "var(--c-danger)", color: "var(--c-card)" },
  }),
  menuPortal: (base) => ({ ...base, zIndex: 1100 }),
};

/** Убирает встроенную сине-серую тему react-select. */
const theme = (base) => ({
  ...base,
  colors: {
    ...base.colors,
    primary: "var(--c-accent)",
    primary25: "var(--c-card-hover)",
    primary50: "var(--c-card-hover)",
    neutral0: "var(--c-card)",
    neutral80: "var(--c-text)",
  },
});

/**
 * @param {Array<{value: any, label: string}>} options
 * @param {any} value — простое значение, не объект
 * @param {(value: any) => void} onChange — получает простое значение
 */
export function Select({
  options = [],
  value,
  onChange,
  placeholder = "Выберите…",
  isClearable = false,
  isDisabled = false,
  isSearchable = true,
  name,
  inputId,
  className,
  menuPortalTarget,
  ...rest
}) {
  const selected = useMemo(
    () => options.find((o) => String(o.value) === String(value)) ?? null,
    [options, value]
  );

  return (
    <ReactSelect
      options={options}
      value={selected}
      onChange={(opt) => onChange?.(opt ? opt.value : null)}
      placeholder={placeholder}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isSearchable={isSearchable}
      name={name}
      inputId={inputId}
      className={className}
      classNamePrefix="ui-select"
      styles={styles}
      theme={theme}
      noOptionsMessage={() => "Ничего не найдено"}
      loadingMessage={() => "Загрузка…"}
      menuPortalTarget={menuPortalTarget}
      {...rest}
    />
  );
}

export default Select;
